import { useState, useEffect } from 'react';
import { X, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

const StudentSelectorModal = ({ isOpen, onClose, onConfirm, hostelId, preselected = [] }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(preselected);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    class: '',
    minAge: '',
    maxAge: '',
    room: ''
  });
  const [classes, setClasses] = useState([]);
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
    }
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [students, filters]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/v1/admin/students/', {
        params: { hostel_id: hostelId }
      });
      const studentsData = Array.isArray(response.data) ? response.data : [];
      setStudents(studentsData);
      
      // Extract unique classes and rooms
      const uniqueClasses = [...new Set(studentsData.map(s => s.class_name).filter(Boolean))];
      const uniqueRooms = [...new Set(studentsData.map(s => s.room_number).filter(Boolean))];
      setClasses(uniqueClasses.sort());
      setRooms(uniqueRooms.sort());
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchLower) ||
        (s.readable_id && s.readable_id.toLowerCase().includes(searchLower))
      );
    }

    if (filters.class) {
      filtered = filtered.filter(s => s.class_name === filters.class);
    }

    if (filters.room) {
      filtered = filtered.filter(s => s.room_number === filters.room);
    }

    if (filters.minAge) {
      filtered = filtered.filter(s => s.age >= parseInt(filters.minAge));
    }

    if (filters.maxAge) {
      filtered = filtered.filter(s => s.age <= parseInt(filters.maxAge));
    }

    setFilteredStudents(filtered);
  };

  const handleToggle = (studentId) => {
    setSelectedIds(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredStudents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredStudents.map(s => s.id));
    }
  };

  const handleConfirm = () => {
    const selected = students.filter(s => selectedIds.includes(s.id));
    onConfirm(selected);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#2C5F7F' }}>
              Select Students
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectedIds.length} of {filteredStudents.length} students selected
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
            {/* Search */}
            <div>
              <label className="block text-xs font-medium mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Name or ID"
                  className="w-full pl-8 pr-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Class */}
            <div>
              <label className="block text-xs font-medium mb-1">Class</label>
              <select
                value={filters.class}
                onChange={(e) => setFilters({ ...filters, class: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                {classes.map(cls => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>

            {/* Room */}
            <div>
              <label className="block text-xs font-medium mb-1">Room</label>
              <select
                value={filters.room}
                onChange={(e) => setFilters({ ...filters, room: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                {rooms.map(room => <option key={room} value={room}>{room}</option>)}
              </select>
            </div>

            {/* Min Age */}
            <div>
              <label className="block text-xs font-medium mb-1">Min Age</label>
              <input
                type="number"
                value={filters.minAge}
                onChange={(e) => setFilters({ ...filters, minAge: e.target.value })}
                placeholder="Min"
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Max Age */}
            <div>
              <label className="block text-xs font-medium mb-1">Max Age</label>
              <input
                type="number"
                value={filters.maxAge}
                onChange={(e) => setFilters({ ...filters, maxAge: e.target.value })}
                placeholder="Max"
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedIds.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </div>

        {/* Students List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No students found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStudents.map(student => {
                const isSelected = selectedIds.includes(student.id);
                // Get parent phone if available
                const parentPhone = student.parent_phone || 'N/A';
                
                return (
                  <div
                    key={student.id}
                    onClick={() => handleToggle(student.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="font-semibold">{student.name}</p>
                            <p className="text-sm text-gray-600">
                              Class: {student.class_name} • Age: {student.age} • Room: {student.room_number}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Parent Phone: {parentPhone}
                            </p>
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            className="btn-primary text-white"
          >
            <Users className="w-4 h-4 mr-2" />
            Confirm ({selectedIds.length})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StudentSelectorModal;
